/**
 * VR "Share to screen" panel: choose a room screen, type a link on the 3D
 * keyboard (or tap YouTube), Go → it plays on that screen for everyone.
 * Opened from the VR user menu; replaces the menu while open.
 * Sharing a link is a Creator Council perk: for non-members the link field,
 * YouTube, Paste and Go are greyed out and the status explains why (viewers
 * can still type on / control what's already playing).
 */
import React, { useEffect, useState } from 'react';
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { vrColors as colors, noRaycast, Surface } from './vrUi';
import VRKeyboard, { KEY, type KeyAction } from './VRKeyboard';
import type { ScreenPropItem } from '../../stores/worldStore';
import { useWorldStore } from '../../stores/worldStore';
import {
  canShareToScreens, canStopScreen, controlVideo, describeAllowance, fetchScreenAccess, shareToScreen, stopScreen, typeToScreen, useWatchingCount,
  useRoomScreenStore, useScreenLinkLocked,
} from '../../services/roomScreens';

export interface ShareTarget { id: string; item: ScreenPropItem }

function Button({ label, x, y, w = .17, primary = false, disabled = false, onPress }: {
  label: string; x: number; y: number; w?: number; primary?: boolean; disabled?: boolean; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = disabled ? '#1a2633' : primary ? (hover ? '#2ee6d6' : colors.teal) : hover ? colors.hover : colors.card;
  return <group position={[x, y, .006]} name={`vr-share-${label}`}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={.056} color={bg} r={.012} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={.019} color={disabled ? colors.muted : primary ? '#04211e' : colors.text}>{label}</Text>
  </group>;
}

const LOCKED_VR = 'Sharing a link to the screen is a Creator Council perk (gathrxr.com/council). Tip: on a computer, open the video in a browser tab and use Share my screen — headsets see it too.';

function hostOf(url: string) { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; } }

export default function VRSharePanel({ targets, initialTarget, isOwner = false, onClose }: {
  targets: ShareTarget[]; initialTarget?: string; isOwner?: boolean; onClose: () => void;
}) {
  const [targetId, setTargetId] = useState(initialTarget || targets[0]?.id);
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // "Type on screen": the keyboard drives the cloud browser (search boxes,
  // forms) instead of the link field.
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState('');
  const screen = useRoomScreenStore((s) => (targetId ? s.screens[targetId] : undefined));
  const configured = useRoomScreenStore((s) => s.configured);
  const notice = useRoomScreenStore((s) => (targetId ? s.notice[targetId] : ''));
  const myId = useWorldStore((s) => s.user?.id || null);
  const signedIn = canShareToScreens();
  const locked = useScreenLinkLocked() && signedIn;
  const allowance = useRoomScreenStore((s) => describeAllowance(s.access));
  useEffect(() => { void fetchScreenAccess(); }, []);
  useEffect(() => { if (!targetId && targets[0]) setTargetId(targets[0].id); }, [targets, targetId]);
  const canType = screen?.mode === 'browser';
  useEffect(() => { if (!canType) setTyping(false); setTyped(''); }, [canType, targetId]);

  // Blinking caret (~530 ms like a desktop text field).
  const blink = React.useRef(0);
  useFrame((_, dt) => {
    blink.current += dt;
    if (blink.current > .53) { blink.current = 0; setCaret((c) => !c); }
  });

  const target = targets.find((t) => t.id === targetId);
  const go = async () => {
    if (locked) { setError(LOCKED_VR); return; }
    if (!target || !text.trim() || busy) return;
    setBusy(true); setError('');
    try {
      await shareToScreen(target.id, text, { width: target.item.width, height: target.item.height });
      setText('');
      onClose();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not share'); }
    finally { setBusy(false); }
  };
  const onKey = (a: KeyAction) => {
    setError('');
    blink.current = 0; setCaret(true);
    if (typing && targetId) {
      const ok = typeToScreen(targetId, a.type === 'backspace' ? { key: 'Backspace' } : a.type === 'go' ? { key: 'Enter' } : { text: a.value });
      if (!ok) { setError('The screen is still connecting'); return; }
      if (a.type === 'backspace') setTyped((t) => t.slice(0, -1));
      else if (a.type === 'go') setTyped('');
      else setTyped((t) => (t + a.value).slice(-200));
      return;
    }
    if (locked) { setError(LOCKED_VR); return; }
    if (a.type === 'backspace') setText((t) => t.slice(0, -1));
    else if (a.type === 'go') void go();
    else setText((t) => (t + a.value).slice(0, 300));
  };

  // Paste from the headset / computer clipboard (needs a user gesture; the
  // browser may ask once for clipboard permission).
  const paste = async () => {
    setError('');
    let clip = '';
    try { clip = (await navigator.clipboard?.readText?.()) || ''; } catch { /* denied or unsupported */ }
    clip = clip.replace(/[\r\n]+/g, ' ').trim();
    if (!clip) { setError('Nothing to paste. Copy a link first (allow clipboard access if asked).'); return; }
    if (locked && !typing) { setError(LOCKED_VR); return; }
    if (typing && targetId) {
      if (!typeToScreen(targetId, { text: clip })) { setError('The screen is still connecting'); return; }
      setTyped((t) => (t + clip).slice(-200));
    } else setText((t) => (t + clip).slice(0, 300));
  };

  // The sharer of a cloud browser sees how many accounts they're paying for.
  const mineBrowser = !!screen && screen.mode === 'browser' && !!myId && screen.startedBy === myId;
  const watching = useWatchingCount(targetId ?? null, mineBrowser);

  // Field shows the tail of long links.
  const shown = text.length > 36 ? `…${text.slice(-35)}` : text;
  const status = error || (typing ? 'Typing goes to the screen. Point at a search box or field on the screen and click it first.' : '') || notice || (!signedIn ? 'Sign in on this headset to share to the screen.'
    : locked ? LOCKED_VR
    : busy ? 'Starting…'
      : configured === false ? "Cloud browser isn't set up yet. Video file links (.mp4, .m3u8) still work."
        : screen ? `Now: ${hostOf(screen.url)}${mineBrowser ? (watching !== null ? ` · Watching: ${watching}` : '') : screen.startedByName ? ` · shared by ${screen.startedByName}` : ''}` : `Type a link or tap YouTube, then Go.${allowance ? ` ${allowance}` : ''}`);

  const W = .8, top = .34, bottom = -.39;
  const H = top - bottom;
  return <group onClick={(e) => e.stopPropagation()} name="vr-share-panel">
    <group position={[0, (top + bottom) / 2, 0]}>
      <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} />
      <Surface width={W} height={H} color={colors.panel} />
    </group>
    <Text raycast={noRaycast} position={[-.37, .305, .006]} anchorX="left" fontSize={.024} color={colors.text}>Share to screen</Text>
    {locked && <group position={[-.115, .305, .006]}>
      <Surface width={.1} height={.032} color="#3a3216" r={.008} />
      <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={.014} color="#ffd166">COUNCIL</Text>
    </group>}
    <Button label="Close" x={.32} y={.305} w={.12} onPress={onClose} />

    {/* Screen picker — nearest first */}
    <Text raycast={noRaycast} position={[-.37, .245, .006]} anchorX="left" fontSize={.015} color={colors.muted}>Screen</Text>
    {targets.slice(0, 3).map((t, i) => <group key={t.id}>
      <Button label={`${t.id === targetId ? '● ' : ''}${(t.item.name || `Screen ${i + 1}`).slice(0, 18)}${i === 0 ? ' (nearest)' : ''}`}
        x={-.18 + i * .2 + (targets.length === 1 ? .1 : 0)} y={.245} w={targets.length === 1 ? .38 : .19} onPress={() => setTargetId(t.id)} />
    </group>)}

    {/* URL field */}
    <group position={[-.065, .172, .006]}>
      <Surface width={.61} height={.066} color="#0a141f" r={.012} />
      <group position={[0, 0, -.001]}><Surface width={.616} height={.072} color={colors.teal} r={.014} /></group>
      {typing
        ? <Text raycast={noRaycast} position={[-.29, 0, .003]} anchorX="left" fontSize={.022} color={typed ? colors.text : colors.muted}>
          {typed ? `${typed.length > 36 ? `…${typed.slice(-35)}` : typed}${caret ? '▏' : ' '}` : `${caret ? '▏' : ' '}Typing on the screen…`}
        </Text>
        : <Text raycast={noRaycast} position={[-.29, 0, .003]} anchorX="left" fontSize={.022} color={text ? colors.text : colors.muted}>
          {locked ? 'Share link is a Council perk' : text ? `${shown}${caret ? '▏' : ' '}` : `${caret ? '▏' : ' '}youtube.com/watch?v=… or a link`}
        </Text>}
    </group>

    <Button label="Paste" x={.315} y={.172} w={.12} disabled={locked && !typing} onPress={() => void paste()} />

    {/* Quick actions */}
    {canType
      ? <Button label={typing ? '● Typing' : 'Type on screen'} w={.17} x={-.285} y={.098} onPress={() => { setTyping((v) => !v); setTyped(''); setError(''); }} />
      : <Button label="YouTube" x={-.285} y={.098} disabled={locked} onPress={() => setText('https://www.youtube.com')} />}
    {typing
      ? <Button label="Tab" x={-.095} y={.098} onPress={() => { if (targetId) typeToScreen(targetId, { key: 'Tab' }); }} />
      : <Button label="Clear" x={-.095} y={.098} disabled={locked} onPress={() => setText('')} />}
    {screen?.mode === 'video' && screen.video
      ? <Button label={screen.video.playing ? 'Pause' : 'Play'} x={.095} y={.098} onPress={() => targetId && controlVideo(targetId, { playing: !screen.video!.playing })} />
      : typing
        ? <Button label="Share a link" x={.095} y={.098} onPress={() => { setTyping(false); setTyped(''); }} />
        : <Button label={busy ? 'Starting…' : 'Go'} x={.095} y={.098} primary disabled={!text.trim() || busy || !signedIn || locked} onPress={() => void go()} />}
    <Button label="Stop sharing" x={.285} y={.098} disabled={!screen || !canStopScreen(screen, isOwner, myId)}
      onPress={() => { if (targetId) void stopScreen(targetId).catch((e) => setError(e.message)); }} />

    <Text raycast={noRaycast} position={[-.37, .045, .006]} anchorX="left" fontSize={.015} maxWidth={.74}
      color={error ? '#ffb1ad' : colors.muted}>{status}</Text>

    <group position={[0, -.005 - KEY / 2, .006]}>
      <VRKeyboard onKey={onKey} goLabel={typing ? 'Enter' : 'Go'} />
    </group>
  </group>;
}
